import { IDatabaseColumnModel } from "@kuika/kuika-cl-model";
import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  IManagedDbColumnFilter,
  IManagedDbForeignKey,
  IManagedDbInboundForeignKey,
  IManagedDbRow,
  IManagedDbRowsResponse,
  TableEditorService
} from "../../../services/table-editor-service";
import { buildColumnFilters, IColumnFilterMap } from "../column-filters";
import { IGridSortState } from "../editable-data-grid";
import { buildTableEditorErrorAlert, ITableEditorAlert } from "../table-editor-errors";

// KOA-5857 — Rows / paging / refresh hook for the Table Editor.
//
// Pulled out of TableRowsEditor to keep the parent a composition root.
// Owns:
//   * QueryRows + the AbortController that drops a stale page-flip
//   * columns / primaryKeyColumns / foreignKeys / rows / totalCount state
//   * isLoading + loadError surfaces (plus a setter for save-flow errors
//     that share the same banner real estate)
//   * the auto-refresh useEffect that re-fires on input change
//
// Does NOT own:
//   * page / size / sort / filter state — those are input props because
//     they're shared with toolbar controls in the parent
//   * selection pruning — the parent observes `rows` and prunes its own
//     selection set. Keeping cross-cutting concerns at the composition
//     root avoids the hook taking a callback bag.

export interface IUseTableRowsQueryProps {
  tableName: string;
  tableSchema?: string;
  pageNumber: number;
  pageSize: number;
  sort: IGridSortState | null;
  debouncedFilters: IColumnFilterMap;
}

export interface IUseTableRowsQueryResult {
  columns: IDatabaseColumnModel[];
  primaryKeyColumns: string[];
  // True for tables on the server's exact KuikaTables list. Drives the
  // read-only banner / disabled affordances on the editor. Defaults to
  // false until the first response lands so the editor doesn't treat
  // an unloaded table as read-only.
  isSystemTable: boolean;
  foreignKeys: IManagedDbForeignKey[];
  inboundForeignKeys: IManagedDbInboundForeignKey[];
  // Names of columns the server stripped from `columns` / row payloads
  // because they matched the sensitive-column policy. Surfaced as a
  // visible toolbar affordance — the previous behaviour silently
  // dropped these columns, which left users wondering where a column
  // they knew existed had gone. Empty when nothing was stripped.
  sensitiveHiddenColumns: string[];
  rows: IManagedDbRow[];
  totalCount: number;
  // Pagination shape from the most recently committed response — NOT
  // the in-flight UI selection. Source-of-truth for any derivation
  // that mixes row position with absolute offsets (gutter index,
  // pagination "showing X-Y of Z" label). Sourcing these from the
  // live `pageNumber` / `pageSize` props produces a brief mismatch
  // during page flips: the offset updates immediately while the rows
  // arrive on the next tick, so a 25-page-size, 32-row table shows
  // indices 26-50 for ~100 ms before the page-2 rows replace the
  // stale page-1 rows. These fields hold the values that produced
  // the rows currently in state — they only update when the new
  // response lands, so the labels can't get ahead of the data.
  loadedPageNumber: number;
  loadedPageSize: number;
  isLoading: boolean;
  loadError: ITableEditorAlert | null;
  refreshRows: () => Promise<void>;
  setLoadError: (error: ITableEditorAlert | null) => void;
}

export const useTableRowsQuery = (props: IUseTableRowsQueryProps): IUseTableRowsQueryResult => {
  const { tableName, tableSchema, pageNumber, pageSize, sort, debouncedFilters } = props;

  const [columns, setColumns] = useState<IDatabaseColumnModel[]>([]);
  const [primaryKeyColumns, setPrimaryKeyColumns] = useState<string[]>([]);
  const [foreignKeys, setForeignKeys] = useState<IManagedDbForeignKey[]>([]);
  const [inboundForeignKeys, setInboundForeignKeys] = useState<IManagedDbInboundForeignKey[]>([]);
  const [isSystemTable, setIsSystemTable] = useState<boolean>(false);
  const [sensitiveHiddenColumns, setSensitiveHiddenColumns] = useState<string[]>([]);
  const [rows, setRows] = useState<IManagedDbRow[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  // Pagination shape that produced the current `rows`. Initialised
  // to the first-render selection so the editor renders consistent
  // labels before the first response arrives; thereafter committed
  // after every successful query so they're always in sync with the
  // data actually on screen.
  const [loadedPageNumber, setLoadedPageNumber] = useState<number>(pageNumber);
  const [loadedPageSize, setLoadedPageSize] = useState<number>(pageSize);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<ITableEditorAlert | null>(null);

  // Holds the AbortController for the in-flight refresh. Replaces the
  // earlier sequence-guard pattern (which ignored late responses but still
  // let the browser parse them) — abort() now drops the request at the
  // network layer too. Cleared after the request settles, OR after the
  // effect cleanup fires on table switch / unmount.
  const refreshAbortRef = useRef<AbortController | null>(null);

  const refreshRows = useCallback(async (): Promise<void> => {
    // Cancel any in-flight refresh before starting a new one. Without this
    // a fast page-flip would race: the slow first request resolves into
    // state AFTER the fast second one, showing page 1 data labelled as
    // page 2. AbortController guarantees the late response can't fire
    // setSomething at all.
    refreshAbortRef.current?.abort();
    const controller = new AbortController();
    refreshAbortRef.current = controller;
    setIsLoading(true);
    setLoadError(null);
    try {
      const filters: IManagedDbColumnFilter[] = buildColumnFilters(debouncedFilters);
      const response = await TableEditorService.QueryRows(
        tableName,
        {
          tableSchema,
          pageNumber,
          pageSize,
          sortColumn: sort?.column,
          sortDir: sort?.dir,
          filters
        },
        controller.signal
      );
      if (controller.signal.aborted) {
        return;
      }
      const payload: IManagedDbRowsResponse = response.data;
      setColumns(payload.columns ?? []);
      setPrimaryKeyColumns(payload.primaryKeyColumns ?? []);
      setForeignKeys(payload.foreignKeys ?? []);
      setInboundForeignKeys(payload.inboundForeignKeys ?? []);
      setIsSystemTable(payload.isSystemTable === true);
      setSensitiveHiddenColumns(payload.hiddenColumns ?? []);
      setRows(payload.rows ?? []);
      setTotalCount(payload.totalCount ?? 0);
      // Commit the pagination shape AT THE SAME TIME as the rows so
      // the gutter / "showing X-Y of Z" labels never get ahead of the
      // data they describe. Sourced from the request the response
      // belongs to, not the live UI state — if the user clicked Next
      // again before this response landed, the next refresh's
      // commit will overwrite this one.
      setLoadedPageNumber(pageNumber);
      setLoadedPageSize(pageSize);
    } catch (error: unknown) {
      // Don't surface aborts as errors — the user just changed page / sort
      // / table, which is the normal path, not a failure.
      if (axios.isCancel(error) || controller.signal.aborted) {
        return;
      }
      const alert: ITableEditorAlert = await buildTableEditorErrorAlert(error, {
        operation: "load",
        title: "Could not load table rows",
        fallbackMessage: "The table data could not be loaded."
      });
      setLoadError(alert);
    } finally {
      if (refreshAbortRef.current === controller) {
        setIsLoading(false);
        refreshAbortRef.current = null;
      }
    }
  }, [tableName, tableSchema, pageNumber, pageSize, sort, debouncedFilters]);

  useEffect((): (() => void) => {
    void refreshRows();
    return (): void => {
      // Table switch / unmount: abort whatever's in flight so the browser
      // stops downloading + parsing a payload nobody will use.
      refreshAbortRef.current?.abort();
      refreshAbortRef.current = null;
    };
  }, [refreshRows]);

  return {
    columns,
    primaryKeyColumns,
    isSystemTable,
    foreignKeys,
    inboundForeignKeys,
    sensitiveHiddenColumns,
    rows,
    totalCount,
    loadedPageNumber,
    loadedPageSize,
    isLoading,
    loadError,
    refreshRows,
    setLoadError
  };
};
